import { computed } from 'mobx';
import sumBy from 'lodash/sumBy';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import type { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import { IPaymentScheduleBlockResponse } from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXPaymentScheduleBlock extends CXContainerableBlock {
    type: 'payment_schedule_block';

    constructor(
        block: IPaymentScheduleBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    override get childBlocks() {
        return super.childBlocks as CXPaymentBlock[];
    }

    @computed
    get totalAmount() {
        return sumBy(this.childBlocks, (payment: CXPaymentBlock) => {
            return payment.paymentAmount;
        });
    }

    @computed
    get shouldDisplayPaymentDateColumn() {
        return this.childBlocks.some((payment: CXPaymentBlock) => {
            return payment.paymentChargeDate;
        });
    }

    get paymentBlocks() {
        return this.childBlocks as CXPaymentBlock[];
    }

    get invoiceBlock() {
        return this.parent as CXInvoiceBlock;
    }

    @computed
    get paymentsBlocksMap() {
        const result = {};
        this.childBlocks.forEach((paymentBlock: CXPaymentBlock) => {
            result[paymentBlock.block_id] = paymentBlock;
        });
        return result;
    }

    @computed
    get unpaidPaymentBlocks(): CXPaymentBlock[] {
        return this.childBlocks.filter(
            payment => !payment.isPaid && !payment.isRefunded
        );
    }

    @computed
    get nextPaymentBlock(): CXPaymentBlock {
        return this.unpaidPaymentBlocks[0];
    }

    @computed
    get fixedPayments() {
        return this.paymentBlocks.filter(payment => payment.isFixed);
    }

    @computed
    get hasPaidPayments() {
        return this.paidPayments.length > 0;
    }

    @computed
    get hasFixedPayments() {
        return this.fixedPayments.length > 0;
    }

    @computed
    get paidPayments() {
        return this.childBlocks.filter((payment: CXPaymentBlock) => {
            return payment.isPaid || payment.isRefunded;
        });
    }
}
